import React, { useState, useEffect, useCallback } from 'react';
import { FieldLabel } from '../components/FieldLabel';
import { useParams, useNavigate } from 'react-router-dom';
import { useBackRoute } from '../hooks/useBackRoute';
import { useAuth } from '../contexts/AuthContext';
import { sanitizeString } from '../utils/security';
import IntegerInput from '../components/IntegerInput';
import { parseAdet } from '../utils/integerInput';
import { 
  fetchAppointmentDetail,
  assignTechnician,
  reassignTechnician,
  cancelAssignment,
  fetchAssignmentHistory,
  addAppointmentOperation,
  addAppointmentReplacedPart,
  addAppointmentIlaveIslem,
  updateAppointmentStatus,
} from '../services/serviceRecordsService';
import { fetchTechniciansByTechService } from '../services/techniciansService';
import { getParcaFotoUrl } from '../services/productsService';
import ParcaSecModal from '../components/ParcaSecModal';
import AppointmentCompletionTab from '../components/AppointmentCompletionTab';
import AppointmentChecklistTab from '../components/AppointmentChecklistTab';
import { shouldShowChecklistTab } from '../utils/appointmentChecklist';
import { fetchTechnicianAvailability } from '../services/technicianAvailabilityService';
import { APPOINTMENT_STATUS } from '../utils/appointmentCategories';
import { getOnemBorderClass, getPriorityToken } from '../utils/onemDerecesiHelper';
import './ServiceRecordUpdatePage.css';

const safeText = (value) => sanitizeString(value ?? '');

const getErrorMessage = (error, fallback) => {
  if (!error) return fallback;

  // Önce backend'den gelen mesajı kontrol et
  let message =
    error?.data?.mesaj ||
    error?.data?.message ||
    error?.data?.error ||
    error?.data?.hata ||
    error?.message;

  // Eğer backend'den mesaj geldiyse ve randevu ile ilgiliyse, onu kullan
  if (message && typeof message === 'string') {
    const lowerMessage = message.toLowerCase();
    if (lowerMessage.includes('randevu') || lowerMessage.includes('appointment')) {
      return message;
    }
  }

  // 404 hatası için özel kontrol
  if (error?.status === 404) {
    // Eğer backend'den mesaj geldiyse onu kullan, yoksa genel mesaj
    return message || 'Randevu bulunamadı.';
  }
  
  if (error?.status === 401) return 'Kimlik doğrulama hatası. Lütfen tekrar giriş yapın.';
  if (error?.status === 403) return 'Bu işlem için yetkiniz yok.';

  // Mesaj yoksa fallback kullan
  if (!message) {
    message = fallback;
  }

  if (message && typeof message === 'string') {
    message = message
      .replace(/&#x27;/g, "'")
      .replace(/&#39;/g, "'")
      .replace(/&quot;/g, '"')
      .replace(/&amp;/g, '&')
      .replace(/&lt;/g, '<')
      .replace(/&gt;/g, '>');
  }

  if (message === 'Failed to fetch') {
    return 'Sunucuya bağlanılamadı. İnternet bağlantınızı kontrol edin.';
  }

  return message || fallback;
};

const getDeviceName = (device = {}) => {
  return (
    device.urun?.icerikAdi ||
    device.urun?.urunAdi ||
    device.icerik?.icerikAdi ||
    device.icerik?.urunAdi ||
    device.urunAdi ||
    device.icerikAdi ||
    device.cihazAdi ||
    device.ad ||
    '---'
  );
};

const getDeviceSerial = (device = {}) => {
  return device.seriNumarasi || device.seriNo || device.serial || '---';
};

const TechServiceAppointmentDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const goBack = useBackRoute('/appointments');
  const { user, isLoading: authLoading } = useAuth();
  const [appointment, setAppointment] = useState(null);
  const [serviceRecord, setServiceRecord] = useState(null);
  const [assignmentHistory, setAssignmentHistory] = useState([]);
  const [technicians, setTechnicians] = useState([]);
  const [activeTab, setActiveTab] = useState('record');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [isTechnicianModalOpen, setIsTechnicianModalOpen] = useState(false);
  const [isCancelModalOpen, setIsCancelModalOpen] = useState(false);
  const [selectedTechnicianId, setSelectedTechnicianId] = useState('');
  const [cancelReason, setCancelReason] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [techniciansLoading, setTechniciansLoading] = useState(false);

  // İşlem Başla / Parça Ekle / Değiştirilen Parçalar
  const [actionLoading, setActionLoading] = useState(false);
  const [newOperation, setNewOperation] = useState({ islemAdi: '', aciklama: '' });
  const [newPart, setNewPart] = useState({ parcaAdi: '', parcaKodu: '', adet: 1, fiyat: 0 });
  const [newIlave, setNewIlave] = useState({ islemAdi: '', fiyat: 0, adet: 1 });
  const [isParcaSecModalOpen, setIsParcaSecModalOpen] = useState(false);

  const techServiceId = user?.techService || user?.techServiceId;

  // Randevu detayını yükle
  const loadAppointmentDetail = useCallback(async () => {
    if (!id) return;
    
    setLoading(true);
    setError('');
    try {
      const response = await fetchAppointmentDetail(id);
      
      if (response?.basarili === false) {
        throw new Error(response?.mesaj || 'Randevu detayı yüklenemedi');
      }
      
      // Response yapısını kontrol et ve veriyi çıkar
      const appointmentData = response?.veri || response?.data || response;
      
      setAppointment(appointmentData);
      
      // Servis kaydı bilgilerini çıkar - farklı yapıları kontrol et
      let serviceRecordData = appointmentData?.servisKaydi || 
                              appointmentData?.serviceRecord || 
                              {};
      
      // Eğer servis kaydı ID varsa ama detay yoksa, servis kaydını ayrıca yükle
      if ((!serviceRecordData || Object.keys(serviceRecordData).length === 0) && appointmentData?.servisKaydiId) {
        // Servis kaydını ayrıca yükle
        try {
          const { fetchServiceRecordDetail } = await import('../services/serviceRecordsService');
          const serviceRecordResponse = await fetchServiceRecordDetail(appointmentData.servisKaydiId);
          const serviceRecordDetail = serviceRecordResponse?.veri?.servisKaydi || 
                                     serviceRecordResponse?.veri?.serviceRecord ||
                                     serviceRecordResponse?.veri || {};
          serviceRecordData = serviceRecordDetail;
        } catch (serviceRecordErr) {
          // Devam et, boş obje ile
        }
      }
      
      setServiceRecord(serviceRecordData);
      
      // Atama geçmişini yükle
      try {
        const historyResponse = await fetchAssignmentHistory(id);
        const history = historyResponse?.veri?.atamaGecmisi || 
                       historyResponse?.veri?.assignmentHistory ||
                       historyResponse?.veri || [];
        setAssignmentHistory(Array.isArray(history) ? history : []);
      } catch (err) {
        setAssignmentHistory([]);
      }
    } catch (err) {
      setError(getErrorMessage(err, 'Randevu detayı yüklenemedi'));
      if (window.showNotification) {
        window.showNotification('error', getErrorMessage(err, 'Randevu detayı yüklenemedi'));
      }
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    loadAppointmentDetail();
  }, [loadAppointmentDetail]);

  // Teknisyenleri yükle ve randevu saatinde müsait olanları filtrele
  const loadTechnicians = useCallback(async () => {
    if (!techServiceId || !appointment) return;
    
    setTechniciansLoading(true);
    try {
      const response = await fetchTechniciansByTechService(techServiceId, { aktif: true });
      const list = response?.veri?.teknisyenler || response?.veri || [];
      
      if (!Array.isArray(list) || list.length === 0) {
        setTechnicians([]);
        return;
      }
      
      // Randevu tarih ve saatini al
      const appointmentDate = appointment.randevuTarihi ? new Date(appointment.randevuTarihi) : null;
      const appointmentTime = appointment.randevuSaati;
      
      if (!appointmentDate || !appointmentTime) {
        const currentTechId =
          appointment.teknisyen?.teknisyenId || appointment.teknisyenId || null;
        const filtered = currentTechId
          ? list.filter((tech) => String(tech.id || tech._id) !== String(currentTechId))
          : list;
        setTechnicians(filtered);
        return;
      }
      
      // Tarihi YYYY-MM-DD formatına çevir
      const year = appointmentDate.getFullYear();
      const month = String(appointmentDate.getMonth() + 1).padStart(2, '0');
      const day = String(appointmentDate.getDate()).padStart(2, '0');
      const dateStr = `${year}-${month}-${day}`;
      
      // Her teknisyen için availability kontrolü yap
      const availabilityChecks = await Promise.allSettled(
        list.map(async (tech) => {
          const techId = tech.id || tech._id;
          try {
            // O tarih için kuralları çek (safeApiRequest kullan)
            const availData = await fetchTechnicianAvailability(techId, {
              startDate: dateStr,
              endDate: dateStr
            });
            
            if (!availData || !availData.basarili) {
              return { tech, isAvailable: true };
            }
            
            const kurallar = availData?.veri?.kurallar || [];
            
            // O tarih ve saatte off mu kontrol et
            const isOff = kurallar.some(kural => {
              // Tek seferlik plan kontrolü
              if (!kural.tekrarli && kural.tarih) {
                const ruleDate = new Date(kural.tarih);
                const ruleDateStr = `${ruleDate.getFullYear()}-${String(ruleDate.getMonth() + 1).padStart(2, '0')}-${String(ruleDate.getDate()).padStart(2, '0')}`;
                
                if (ruleDateStr === dateStr) {
                  // Tarih eşleşiyor, şimdi saat kontrolü
                  if (kural.tumGun) {
                    return true; // Tüm gün off
                  }
                  if (Array.isArray(kural.saatler) && kural.saatler.includes(appointmentTime)) {
                    return true; // Bu saat off
                  }
                }
              }
              
              // Tekrarlı plan kontrolü (gun: 0-6)
              if (kural.tekrarli && kural.gun !== null && kural.gun !== undefined) {
                const appointmentDay = appointmentDate.getDay();
                if (kural.gun === appointmentDay) {
                  if (kural.tumGun) {
                    return true;
                  }
                  if (Array.isArray(kural.saatler) && kural.saatler.includes(appointmentTime)) {
                    return true;
                  }
                }
              }
              
              return false;
            });
            
            return { tech, isAvailable: !isOff };
          } catch (err) {
            // Hata durumunda teknisyeni müsait kabul et
            return { tech, isAvailable: true };
          }
        })
      );
      
      // Sadece müsait teknisyenleri filtrele
      const availableTechnicians = availabilityChecks
        .filter(result => result.status === 'fulfilled' && result.value.isAvailable)
        .map(result => result.value.tech);

      const currentTechId =
        appointment.teknisyen?.teknisyenId || appointment.teknisyenId || null;
      const filteredTechnicians = currentTechId
        ? availableTechnicians.filter(
            (tech) => String(tech.id || tech._id) !== String(currentTechId),
          )
        : availableTechnicians;

      setTechnicians(filteredTechnicians);
    } catch (err) {
      setTechnicians([]);
      if (window.showNotification) {
        window.showNotification('error', 'Teknisyenler yüklenemedi');
      }
    } finally {
      setTechniciansLoading(false);
    }
  }, [techServiceId, appointment]);

  // Teknisyen atama modalı açıldığında teknisyenleri yükle
  useEffect(() => {
    if (isTechnicianModalOpen) {
      loadTechnicians();
    }
  }, [isTechnicianModalOpen, loadTechnicians]);

  // Teknisyen ata
  const handleAssignTechnician = async () => {
    if (!selectedTechnicianId) {
      if (window.showNotification) {
        window.showNotification('error', 'Lütfen bir teknisyen seçin');
      }
      return;
    }

    const currentAssignedId =
      appointment?.teknisyen?.teknisyenId || appointment?.teknisyenId || null;
    const isReassign = !!currentAssignedId;

    setSubmitting(true);
    try {
      const response = isReassign
        ? await reassignTechnician(id, selectedTechnicianId)
        : await assignTechnician(id, selectedTechnicianId);
      if (response?.basarili === false) {
        throw new Error(
          response?.mesaj ||
            (isReassign ? 'Teknisyen değiştirilemedi' : 'Teknisyen ataması yapılamadı'),
        );
      }

      if (window.showNotification) {
        window.showNotification(
          'success',
          isReassign ? 'Teknisyen değiştirildi' : 'Teknisyen başarıyla atandı',
        );
      }

      await loadAppointmentDetail();
      
      setIsTechnicianModalOpen(false);
      setSelectedTechnicianId('');
    } catch (err) {
      if (window.showNotification) {
        window.showNotification(
          'error',
          getErrorMessage(
            err,
            currentAssignedId ? 'Teknisyen değiştirilemedi' : 'Teknisyen ataması yapılamadı',
          ),
        );
      }
    } finally {
      setSubmitting(false);
    }
  };

  // Servis kaydı ve randevu ID'lerini al
  const getIds = useCallback(() => {
    const srId = serviceRecord?.id || serviceRecord?._id || appointment?.servisKaydiId;
    const aptId = id || appointment?.id || appointment?._id;
    return { srId, aptId };
  }, [serviceRecord, appointment, id]);

  // İşlem Başla
  const handleStartWork = async () => {
    const { srId, aptId } = getIds();
    if (!srId || !aptId) {
      if (window.showNotification) window.showNotification('error', 'Servis kaydı veya randevu bilgisi bulunamadı');
      return;
    }
    setActionLoading(true);
    try {
      await updateAppointmentStatus(srId, aptId, 'İşlemde');
      if (window.showNotification) window.showNotification('success', 'İşleme başlandı');
      await loadAppointmentDetail();
      setActiveTab('islemler');
    } catch (err) {
      if (window.showNotification) window.showNotification('error', getErrorMessage(err, 'İşlem başlatılamadı'));
    } finally {
      setActionLoading(false);
    }
  };

  // Yapılan işlem ekle
  const handleAddOperation = async () => {
    if (!newOperation.islemAdi?.trim()) {
      if (window.showNotification) window.showNotification('error', 'İşlem adı zorunludur');
      return;
    }
    const { srId, aptId } = getIds();
    if (!srId || !aptId) return;
    setActionLoading(true);
    try {
      await addAppointmentOperation(srId, aptId, {
        islemAdi: newOperation.islemAdi.trim(),
        aciklama: newOperation.aciklama?.trim() || undefined,
      });
      setNewOperation({ islemAdi: '', aciklama: '' });
      await loadAppointmentDetail();
      if (window.showNotification) window.showNotification('success', 'Yapılan işlem eklendi');
    } catch (err) {
      if (window.showNotification) window.showNotification('error', getErrorMessage(err, 'İşlem eklenemedi'));
    } finally {
      setActionLoading(false);
    }
  };

  // Değiştirilen parça ekle
  const handleAddPart = async () => {
    if (!newPart.parcaAdi?.trim()) {
      if (window.showNotification) window.showNotification('error', 'Parça adı zorunludur');
      return;
    }
    const { srId, aptId } = getIds();
    if (!srId || !aptId) return;
    setActionLoading(true);
    try {
      await addAppointmentReplacedPart(srId, aptId, {
        parcaAdi: newPart.parcaAdi.trim(),
        parcaKodu: newPart.parcaKodu?.trim() || undefined,
        adet: parseAdet(newPart.adet, { min: 1 }),
        fiyat: Number(newPart.fiyat) || 0,
      });
      setNewPart({ parcaAdi: '', parcaKodu: '', adet: 1, fiyat: 0 });
      await loadAppointmentDetail();
      if (window.showNotification) window.showNotification('success', 'Değiştirilen parça eklendi');
    } catch (err) {
      if (window.showNotification) window.showNotification('error', getErrorMessage(err, 'Parça eklenemedi'));
    } finally {
      setActionLoading(false);
    }
  };

  // Parçadan seç modalından seçilenleri ekle
  const handleParcaSecilenleriEkle = async (secilenler) => {
    if (!secilenler?.length) return;
    const { srId, aptId } = getIds();
    if (!srId || !aptId) return;
    setActionLoading(true);
    try {
      for (const p of secilenler) {
        await addAppointmentReplacedPart(srId, aptId, {
          parcaAdi: p.parcaAdi || '',
          parcaKodu: p.parcaKodu || undefined,
          adet: p.adet || 1,
          fiyat: Number(p.fiyat) || 0,
        });
      }
      await loadAppointmentDetail();
      if (window.showNotification) window.showNotification('success', `${secilenler.length} parça eklendi`);
    } catch (err) {
      if (window.showNotification) window.showNotification('error', getErrorMessage(err, 'Parçalar eklenemedi'));
    } finally {
      setActionLoading(false);
    }
  };

  // İlave işlem ekle
  const handleAddIlaveIslem = async () => {
    if (!newIlave.islemAdi?.trim()) {
      if (window.showNotification) window.showNotification('error', 'İlave işlem adı zorunludur');
      return;
    }
    const { aptId } = getIds();
    if (!aptId) return;
    setActionLoading(true);
    try {
      await addAppointmentIlaveIslem(aptId, {
        islemler: [{
          islemAdi: newIlave.islemAdi.trim(),
          fiyat: Number(newIlave.fiyat) || 0,
          adet: parseAdet(newIlave.adet, { min: 1 }),
        }],
      });
      setNewIlave({ islemAdi: '', fiyat: 0, adet: 1 });
      await loadAppointmentDetail();
      if (window.showNotification) window.showNotification('success', 'İlave işlem eklendi');
    } catch (err) {
      if (window.showNotification) window.showNotification('error', getErrorMessage(err, 'İlave işlem eklenemedi'));
    } finally {
      setActionLoading(false);
    }
  };

  // Atamayı iptal et (Teknik Servis seviyesinde)
  const handleCancelAssignment = async () => {
    if (!cancelReason || cancelReason.trim().length < 10) {
      if (window.showNotification) {
        window.showNotification('error', 'İptal gerekçesi en az 10 karakter olmalıdır');
      }
      return;
    }

    setSubmitting(true);
    try {
      // Teknik servis seviyesinde iptal - teknisyen ID gerekmez
      const response = await cancelAssignment(id, cancelReason, 'Teknik Servis İptali');
      if (response?.basarili === false) {
        throw new Error(response?.mesaj || 'Atama iptal edilemedi');
      }

      if (window.showNotification) {
        window.showNotification('success', 'Atama başarıyla iptal edildi');
      }

      // Randevu detayını yeniden yükle
      await loadAppointmentDetail();
      
      // Modal'ı kapat
      setIsCancelModalOpen(false);
      setCancelReason('');
    } catch (err) {
      if (window.showNotification) {
        window.showNotification('error', getErrorMessage(err, 'Atama iptal edilemedi'));
      }
    } finally {
      setSubmitting(false);
    }
  };

  const isTechPerson = (user?.rol || '').toLowerCase() === 'tech-person';

  // Auth ve user yüklenene kadar bekle
  if (authLoading || (!user && !error)) {
    return (
      <main className="main-content">
        <div className="loading-state">
          <div className="loading-spinner"></div>
          <div className="loading-text">Yükleniyor...</div>
        </div>
      </main>
    );
  }

  if (loading) {
    return (
      <main className="main-content">
        <div className="loading-state">
          <div className="loading-spinner"></div>
          <div className="loading-text">Randevu detayı yükleniyor</div>
        </div>
      </main>
    );
  }

  if (error || !appointment) {
    return (
      <main className="main-content">
        <div className="error-state">{safeText(error || 'Randevu bulunamadı')}</div>
      </main>
    );
  }

  // Servis kaydı yoksa veya boşsa, hata gösterme ama bilgileri göstermeye çalış
  const customer = serviceRecord?.musteri || {};
  const customerName = `${customer.adi || customer.name || ''} ${customer.soyadi || customer.surname || ''}`.trim() || '---';
  const customerPhone = customer.telefon || customer.phone || customer.gsm || '---';
  const priorityToken = getPriorityToken(serviceRecord?.onemDerecesi);
  const onemBorderClass = getOnemBorderClass(serviceRecord?.onemDerecesi);
  const selectedDevices =
    (Array.isArray(serviceRecord?.cihazlar) && serviceRecord.cihazlar) ||
    (Array.isArray(serviceRecord?.cihazKayitlari) && serviceRecord.cihazKayitlari) ||
    (Array.isArray(serviceRecord?.cihazBilgileri) && serviceRecord.cihazBilgileri) ||
    [];
  const deviceNotSelected = Boolean(serviceRecord?.cihazSecilmedi);
  const deviceNote = serviceRecord?.cihazAciklama || serviceRecord?.cihazNotu || '';
  // Seçili Arızalar: arizaKayitlari, arizalar veya populate edilmiş arizaIds'den al
  const arizaList = (() => {
    if (Array.isArray(serviceRecord?.arizaKayitlari) && serviceRecord.arizaKayitlari.length > 0) {
      return serviceRecord.arizaKayitlari;
    }
    if (Array.isArray(serviceRecord?.arizalar) && serviceRecord.arizalar.length > 0) {
      return serviceRecord.arizalar;
    }
    const arizaIds = serviceRecord?.arizaIds;
    if (Array.isArray(arizaIds) && arizaIds.length > 0) {
      return arizaIds.map(a => {
        const obj = a && typeof a === 'object' ? a : { _id: a };
        const id = obj._id ? (typeof obj._id === 'object' && obj._id.toString ? obj._id.toString() : String(obj._id)) : String(a);
        const ad = obj.icerikAdi || obj.arizaAdi || obj.urunAdi || obj.ad || '';
        return { id, _id: id, icerikAdi: ad, arizaAdi: ad, ad };
      });
    }
    return [];
  })();
  const arizaNote = serviceRecord?.arizaNotu || '';
  const address = serviceRecord?.adres || {};
  const districtName = address.district?.name || address.district?.ad || '---';
  const cityName = address.city?.name || address.city?.ad || '---';
  const statusToken = (() => {
    const value = String(serviceRecord?.durum || '').toLowerCase();
    if (value.includes('devam')) return 'progress';
    if (value.includes('tamam') || value.includes('bitti')) return 'done';
    if (value.includes('iptal')) return 'cancel';
    return 'new';
  })();

  const showChecklistTab = shouldShowChecklistTab(appointment, serviceRecord);

  // Teknisyen bilgisini önce appointment'tan, yoksa atama geçmişinden al
  const getTechnicianName = () => {
    // Önce appointment.teknisyen'den dene
    if (appointment.teknisyen) {
      const ad = appointment.teknisyen.ad || appointment.teknisyen.name || appointment.teknisyen.adi || '';
      const soyad = appointment.teknisyen.soyad || appointment.teknisyen.surname || appointment.teknisyen.soyadi || '';
      const fullName = `${ad} ${soyad}`.trim();
      if (fullName) return fullName;
    }
    
    // Atama geçmişinden "Teknisyen" tipindeki atamayı bul
    const technicianAssignment = assignmentHistory.find(
      (history) => history.atamaTipi === 'Teknisyen' || history.atamaTipi === 'Teknisyen Ataması'
    );
    
    if (technicianAssignment?.atananAdi) {
      return technicianAssignment.atananAdi;
    }
    
    // Eğer teknisyenId varsa ama populate edilmemişse
    if (appointment.teknisyenId) {
      return 'Atandı (Detay yüklenemedi)';
    }
    
    return null;
  };

  const technicianName = getTechnicianName();
  const hasTechnician = technicianName !== null || appointment.teknisyenId;

  return (
    <main className="main-content service-record-update">
      <div className="content-header">
        <div>
          <button
            type="button"
            className="secondary-btn"
            onClick={goBack}
            style={{ marginBottom: '16px' }}
          >
            <i className="fas fa-arrow-left"></i>
            <span>Geri Dön</span>
          </button>
          <h1>Randevu Detayı</h1>
        </div>
      </div>

      {/* Servis Kaydı Bilgileri - Sadece Görüntüleme */}
      <div className={`service-record-info${onemBorderClass ? ` ${onemBorderClass}` : ''}`} style={{ marginBottom: '24px' }}>
        <div className="info-row">
          <div className="info-item">
            <FieldLabel className="info-label" as="span">Müşteri</FieldLabel>
            <span className="info-value">{customerName || '---'}</span>
            <span className="info-meta">Telefon: {customerPhone}</span>
          </div>
          <div className="info-item">
            <FieldLabel className="info-label" as="span">İşlem Tipi</FieldLabel>
            <span className="info-value">{serviceRecord?.islemTipi || '---'}</span>
          </div>
          <div className="info-item">
            <FieldLabel className="info-label" as="span">Randevu Tarihi</FieldLabel>
            <span className="info-value">
              {appointment.randevuTarihi 
                ? new Date(appointment.randevuTarihi).toLocaleDateString('tr-TR', {
                    day: '2-digit',
                    month: '2-digit',
                    year: 'numeric'
                  })
                : '---'}
            </span>
            <span className="info-meta">Saat: {appointment.randevuSaati || '---'}</span>
          </div>
          <div className="info-item">
            <FieldLabel className="info-label" as="span">Önem Derecesi</FieldLabel>
            <span className={`priority-badge priority-${priorityToken}`}>
              {serviceRecord?.onemDerecesi || '---'}
            </span>
          </div>
          <div className="info-item">
            <FieldLabel className="info-label" as="span">Durum</FieldLabel>
            <span className={`status-chip status-${statusToken}`}>
              {serviceRecord?.durum || '---'}
            </span>
          </div>
        </div>

        <div className="info-section">
          <div className="info-section__label">Adres</div>
          <div className="info-section__value">{address.adres || '---'}</div>
          <div className="info-section__meta">{districtName} / {cityName}</div>
        </div>

        <div className="info-section">
          <div className="info-section__label">Seçilen Cihazlar</div>
          {deviceNotSelected ? (
            <div className="info-section__value">Cihaz seçilmedi</div>
          ) : selectedDevices.length > 0 ? (
            <div className="info-section__list">
              {selectedDevices.map((device, index) => (
                <div key={device.id || device._id || device.seriNumarasi || device.seriNo || index} className="info-section__list-item">
                  <span>{getDeviceName(device)}</span>
                  <span className="info-section__meta">Seri No: {getDeviceSerial(device)}</span>
                </div>
              ))}
            </div>
          ) : (
            <div className="info-section__value">---</div>
          )}
          <div className="info-section__meta" style={{ marginTop: '8px' }}>
            <strong>Cihaz Notu:</strong> {deviceNote || '---'}
          </div>
        </div>

        <div className="info-section">
          <div className="info-section__label">Seçili Arızalar</div>
          {arizaList.length > 0 ? (
            <div className="info-section__tags">
              {arizaList.map((ariza) => (
                <span key={ariza.id || ariza._id} className="tag tag--danger">
                  {ariza.icerikAdi || ariza.arizaAdi || ariza.ad || '---'}
                </span>
              ))}
            </div>
          ) : (
            <div className="info-section__value">---</div>
          )}
        </div>

        <div className="info-section">
          <div className="info-section__label">Arıza Notu</div>
          <div className="info-section__value">{arizaNote || '---'}</div>
        </div>

        {serviceRecord?.notlar && (
          <div className="info-section">
            <div className="info-section__label">Notlar</div>
            <div className="info-section__value">{serviceRecord.notlar}</div>
          </div>
        )}
      </div>

      <div className="status-tabs">
        <button
          type="button"
          className={`status-tab-btn ${activeTab === 'record' ? 'active' : ''}`}
          onClick={() => setActiveTab('record')}
        >
          Kayıt Bilgileri
        </button>
        {appointment.durum === 'İşlemde' && (
          <button
            type="button"
            className={`status-tab-btn ${activeTab === 'islemler' ? 'active' : ''}`}
            onClick={() => setActiveTab('islemler')}
          >
            İşlemler
          </button>
        )}
        {showChecklistTab && (
          <button
            type="button"
            className={`status-tab-btn ${activeTab === 'checklist' ? 'active' : ''}`}
            onClick={() => setActiveTab('checklist')}
          >
            Checklist
          </button>
        )}
        <button
          type="button"
          className={`status-tab-btn ${activeTab === 'assignments' ? 'active' : ''}`}
          onClick={() => setActiveTab('assignments')}
        >
          Atamalar
        </button>
        {appointment.durum === APPOINTMENT_STATUS.COMPLETED && (
          <button
            type="button"
            className={`status-tab-btn ${activeTab === 'tamamlama' ? 'active' : ''}`}
            onClick={() => setActiveTab('tamamlama')}
          >
            Tamamlama
          </button>
        )}
      </div>

      {activeTab === 'record' && (
        <div className="service-record-info">
          <div className="info-section">
            <div className="info-section__label">Randevu Tarihi</div>
            <div className="info-section__value">
              {appointment.randevuTarihi 
                ? new Date(appointment.randevuTarihi).toLocaleDateString('tr-TR', {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric'
                  })
                : '---'}
            </div>
          </div>

          <div className="info-section">
            <div className="info-section__label">Randevu Saati</div>
            <div className="info-section__value">{appointment.randevuSaati || appointment.randevuSaatAraligi || '---'}</div>
          </div>

          <div className="info-section">
            <div className="info-section__label">Randevu Durumu</div>
            <div className="info-section__value">{appointment.durum || '---'}</div>
          </div>

          {/* İptal bilgileri (Randevu İptal Edildi ise) */}
          {(appointment.durum === 'Randevu İptal Edildi' || appointment.iptalNedeni || appointment.iptalEden || appointment.iptalEdenIsim) && (
            <div className="info-section" style={{ marginTop: '12px', padding: '16px', background: '#fef2f2', borderRadius: '8px', border: '1px solid #fecaca' }}>
              <div className="info-section__label" style={{ fontWeight: 600, color: '#b91c1c', marginBottom: '8px' }}>İptal Bilgileri</div>
              <div style={{ fontSize: '14px', display: 'flex', flexDirection: 'column', gap: '6px' }}>
                {appointment.iptalNedeni && (
                  <div><strong>İptal Nedeni:</strong> {appointment.iptalNedeni}</div>
                )}
                {(appointment.iptalEdenIsim || appointment.iptalEden) && (
                  <div><strong>İptal Eden:</strong> {appointment.iptalEdenIsim || appointment.iptalEden || '—'}</div>
                )}
                {appointment.iptalTarihi && (
                  <div><strong>İptal Tarihi:</strong> {new Date(appointment.iptalTarihi).toLocaleString('tr-TR')}</div>
                )}
              </div>
            </div>
          )}

          <div className="info-section">
            <div className="info-section__label">Atanan Teknisyen</div>
            <div className="info-section__value">{technicianName || 'Atanmadı'}</div>
          </div>

          {appointment.aciklama && (
            <div className="info-section">
              <div className="info-section__label">Açıklama</div>
              <div className="info-section__value">{appointment.aciklama}</div>
            </div>
          )}

          {/* İşlem Başla - randevu durumu "Randevu Tarihi Bekleniyor" olduğunda */}
          {appointment.durum === 'Randevu Tarihi Bekleniyor' && technicianName && (
            <div style={{ marginTop: '24px', marginBottom: '12px' }}>
              <button
                type="button"
                className="primary-btn"
                onClick={handleStartWork}
                disabled={actionLoading}
              >
                <i className="fas fa-play" style={{ marginRight: '8px' }}></i>
                {actionLoading ? 'Başlatılıyor...' : 'İşlem Başla'}
              </button>
            </div>
          )}

          {/* Atamayı İptal Et — yalnızca teknik servis */}
          {!isTechPerson && appointment.durum === 'Randevu Tarihi Bekleniyor' && (
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px', marginTop: '24px', flexWrap: 'wrap' }}>
              <button
                type="button"
                className="secondary-btn"
                onClick={() => setIsCancelModalOpen(true)}
                style={{ backgroundColor: '#ef4444', color: '#ffffff', borderColor: '#ef4444' }}
              >
                <i className="fas fa-user-minus"></i>
                <span>Atamayı İptal Et</span>
              </button>
            </div>
          )}
        </div>
      )}

      {activeTab === 'islemler' && appointment.durum === 'İşlemde' && (() => {
        const yapilanIslemler = appointment.yapilanIslemler ?? [];
        const degistirilenParcalar = appointment.degistirilenParcalar ?? [];
        const ilaveIslemler = appointment.ilaveIslemler ?? [];
        return (
          <div className="service-record-info" style={{ padding: '20px 0' }}>
            {/* Yapılan İşlemler */}
            <div style={{ marginBottom: '24px' }}>
              <h3 style={{ marginBottom: '12px', fontSize: '16px', fontWeight: 600 }}>Yapılan İşlemler</h3>
              {yapilanIslemler.length > 0 && (
                <ul style={{ marginBottom: '12px', paddingLeft: '20px' }}>
                  {yapilanIslemler.map((o, i) => (
                    <li key={i}>{safeText(o.islemAdi ?? o.icerikAdi)} {o.aciklama && `– ${safeText(o.aciklama)}`}</li>
                  ))}
                </ul>
              )}
              <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', alignItems: 'flex-end' }}>
                <input
                  type="text"
                  placeholder="İşlem adı"
                  value={newOperation.islemAdi}
                  onChange={(e) => setNewOperation((p) => ({ ...p, islemAdi: e.target.value }))}
                  style={{ padding: '8px 12px', borderRadius: '6px', border: '1px solid #e2e8f0', minWidth: '140px' }}
                />
                <input
                  type="text"
                  placeholder="Açıklama (opsiyonel)"
                  value={newOperation.aciklama}
                  onChange={(e) => setNewOperation((p) => ({ ...p, aciklama: e.target.value }))}
                  style={{ padding: '8px 12px', borderRadius: '6px', border: '1px solid #e2e8f0', minWidth: '160px' }}
                />
                <button type="button" className="category-action-btn edit-btn" onClick={handleAddOperation} disabled={actionLoading}>
                  Ekle
                </button>
              </div>
            </div>

            {/* Değiştirilen Parçalar - Parça fotoğrafları ile */}
            <div style={{ marginBottom: '24px' }}>
              <h3 style={{ marginBottom: '12px', fontSize: '16px', fontWeight: 600 }}>Değiştirilen Parçalar</h3>
              {degistirilenParcalar.length > 0 && (
                <div style={{ marginBottom: '12px', display: 'flex', flexDirection: 'column', gap: '12px' }}>
                  {degistirilenParcalar.map((p, i) => {
                    const fotoUrl = getParcaFotoUrl(p.fotoUrl ?? p.icerik?.fotoUrl);
                    const parcaAdi = p.parcaAdi ?? p.icerikAdi ?? '—';
                    const parcaKodu = p.parcaKodu ?? p.icerikKodu ?? '';
                    const adet = p.adet ?? 1;
                    const fiyat = p.fiyat != null ? p.fiyat : '';
                    return (
                      <div
                        key={i}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '16px',
                          padding: '12px 16px',
                          background: '#f8fafc',
                          borderRadius: '8px',
                          border: '1px solid #e2e8f0',
                        }}
                      >
                        {fotoUrl ? (
                          <img
                            src={fotoUrl}
                            alt={parcaAdi}
                            style={{
                              width: 56,
                              height: 56,
                              objectFit: 'cover',
                              borderRadius: '8px',
                              flexShrink: 0,
                            }}
                          />
                        ) : (
                          <div
                            style={{
                              width: 56,
                              height: 56,
                              background: '#e2e8f0',
                              borderRadius: '8px',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              flexShrink: 0,
                            }}
                          >
                            <i className="fas fa-cog" style={{ color: '#94a3b8', fontSize: '20px' }}></i>
                          </div>
                        )}
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontWeight: 600, color: 'var(--text-dark)' }}>{parcaAdi}</div>
                          {(parcaKodu || adet || fiyat) && (
                            <div style={{ fontSize: '13px', color: 'var(--text-gray)', marginTop: '2px' }}>
                              {parcaKodu && <span>Kod: {parcaKodu}</span>}
                              {parcaKodu && (adet || fiyat) && ' · '}
                              {adet && <span>Adet: {adet}</span>}
                              {fiyat !== '' && fiyat != null && <span> · {Number(fiyat).toLocaleString('tr-TR')} ₺</span>}
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
              <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', alignItems: 'flex-end', marginBottom: '8px' }}>
                <button
                  type="button"
                  className="secondary-btn"
                  onClick={() => setIsParcaSecModalOpen(true)}
                  style={{ padding: '8px 14px' }}
                >
                  <i className="fas fa-search" style={{ marginRight: '6px' }}></i>
                  Parçadan Seç
                </button>
              </div>
              <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', alignItems: 'flex-end' }}>
                <input
                  type="text"
                  placeholder="Parça adı"
                  value={newPart.parcaAdi}
                  onChange={(e) => setNewPart((p) => ({ ...p, parcaAdi: e.target.value }))}
                  style={{ padding: '8px 12px', borderRadius: '6px', border: '1px solid #e2e8f0', minWidth: '120px' }}
                />
                <input
                  type="text"
                  placeholder="Kod"
                  value={newPart.parcaKodu}
                  onChange={(e) => setNewPart((p) => ({ ...p, parcaKodu: e.target.value }))}
                  style={{ padding: '8px 12px', borderRadius: '6px', border: '1px solid #e2e8f0', width: '80px' }}
                />
                <IntegerInput
                  min={1}
                  placeholder="Adet"
                  value={newPart.adet}
                  onChange={(v) => setNewPart((p) => ({ ...p, adet: v }))}
                  style={{ padding: '8px 12px', borderRadius: '6px', border: '1px solid #e2e8f0', width: '70px' }}
                />
                <input
                  type="number"
                  min={0}
                  step="0.01"
                  placeholder="Fiyat"
                  value={newPart.fiyat}
                  onChange={(e) => setNewPart((p) => ({ ...p, fiyat: e.target.value }))}
                  style={{ padding: '8px 12px', borderRadius: '6px', border: '1px solid #e2e8f0', width: '90px' }}
                />
                <button type="button" className="category-action-btn edit-btn" onClick={handleAddPart} disabled={actionLoading}>
                  <i className="fas fa-plus" style={{ marginRight: '6px' }}></i>
                  Parça Ekle
                </button>
              </div>
            </div>

            {/* İlave İşlemler */}
            <div>
              <h3 style={{ marginBottom: '12px', fontSize: '16px', fontWeight: 600 }}>İlave İşlemler</h3>
              {ilaveIslemler.length > 0 && (
                <ul style={{ marginBottom: '12px', paddingLeft: '20px' }}>
                  {ilaveIslemler.map((il, i) => (
                    <li key={i}>{safeText(il.islemAdi)} – {il.adet || 1} adet, {Number(il.fiyat ?? 0).toLocaleString('tr-TR')} ₺</li>
                  ))}
                </ul>
              )}
              <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', alignItems: 'flex-end' }}>
                <input
                  type="text"
                  placeholder="İlave işlem adı"
                  value={newIlave.islemAdi}
                  onChange={(e) => setNewIlave((p) => ({ ...p, islemAdi: e.target.value }))}
                  style={{ padding: '8px 12px', borderRadius: '6px', border: '1px solid #e2e8f0', minWidth: '140px' }}
                />
                <input
                  type="number"
                  min={0}
                  step="0.01"
                  placeholder="Fiyat"
                  value={newIlave.fiyat}
                  onChange={(e) => setNewIlave((p) => ({ ...p, fiyat: e.target.value }))}
                  style={{ padding: '8px 12px', borderRadius: '6px', border: '1px solid #e2e8f0', width: '90px' }}
                />
                <IntegerInput
                  min={1}
                  placeholder="Adet"
                  value={newIlave.adet}
                  onChange={(v) => setNewIlave((p) => ({ ...p, adet: v }))}
                  style={{ padding: '8px 12px', borderRadius: '6px', border: '1px solid #e2e8f0', width: '70px' }}
                />
                <button type="button" className="category-action-btn edit-btn" onClick={handleAddIlaveIslem} disabled={actionLoading}>
                  İlave Ekle
                </button>
              </div>
            </div>
          </div>
        );
      })()}

      {activeTab === 'tamamlama' && appointment.durum === APPOINTMENT_STATUS.COMPLETED && (
        <AppointmentCompletionTab appointment={appointment} />
      )}

      {activeTab === 'checklist' && showChecklistTab && (
        <AppointmentChecklistTab appointment={appointment} />
      )}

      {activeTab === 'assignments' && (
        <div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
            <h2 style={{ fontSize: '18px', fontWeight: 600, color: 'var(--text-dark)', margin: 0 }}>Atamalar</h2>
            {appointment.durum === 'Randevu Tarihi Bekleniyor' && (
              <button
                type="button"
                className="primary-btn"
                onClick={() => setIsTechnicianModalOpen(true)}
              >
                <i className="fas fa-user-plus"></i>
                <span>{hasTechnician ? 'Teknisyeni Değiştir' : 'Teknisyen Ata'}</span>
              </button>
            )}
          </div>

          {assignmentHistory.length === 0 ? (
            <div className="empty-state" style={{ padding: '40px', textAlign: 'center' }}>
              <i className="fas fa-history" style={{ fontSize: '32px', color: 'var(--text-light)', marginBottom: '12px' }}></i>
              <div style={{ color: 'var(--text-gray)', fontSize: '14px' }}>Atama geçmişi bulunamadı</div>
            </div>
          ) : (
            <div className="table-container">
              <table className="admins-table">
                <thead>
                  <tr>
                    <th>Tarih</th>
                    <th>Atama Tipi</th>
                    <th>Yapan Kişi</th>
                    <th>Gerekçe</th>
                  </tr>
                </thead>
                <tbody>
                  {assignmentHistory.map((history) => {
                    const historyId = history.id || history._id;
                    const yapanKisi = history.yapanKisi || {};
                    const yapanKisiAdi = `${yapanKisi.ad || yapanKisi.name || ''} ${yapanKisi.soyad || yapanKisi.surname || ''}`.trim() || yapanKisi.kullaniciAdi || '—';
                    const tarih = history.tarih 
                      ? new Date(history.tarih).toLocaleString('tr-TR', {
                          year: 'numeric',
                          month: 'short',
                          day: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit'
                        })
                      : '—';
                    
                    return (
                      <tr key={historyId}>
                        <td>{tarih}</td>
                        <td>{history.atamaTipi || '—'}</td>
                        <td>{yapanKisiAdi}</td>
                        <td>{history.gerekce || '—'}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Teknisyen Atama Modal */}
      {isTechnicianModalOpen && (
        <div className="modal-overlay" onClick={() => !submitting && setIsTechnicianModalOpen(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{hasTechnician ? 'Teknisyeni Değiştir' : 'Teknisyen Ata'}</h2>
              <button
                type="button"
                className="modal-close"
                onClick={() => !submitting && setIsTechnicianModalOpen(false)}
                disabled={submitting}
              >
                <i className="fas fa-times"></i>
              </button>
            </div>
            <div className="modal-body">
              {techniciansLoading ? (
                <div className="loading-state" style={{ padding: '20px' }}>
                  <div className="loading-spinner"></div>
                  <div className="loading-text">Teknisyenler yükleniyor</div>
                </div>
              ) : technicians.length === 0 ? (
                <div className="empty-state" style={{ padding: '20px', textAlign: 'center' }}>
                  <div style={{ color: 'var(--text-gray)', fontSize: '14px' }}>Teknisyen bulunamadı</div>
                </div>
              ) : (
                <>
                  <div className="form-group">
                    <label htmlFor="technician-select">Teknisyen Seçin</label>
                    <select
                      id="technician-select"
                      value={selectedTechnicianId}
                      onChange={(e) => setSelectedTechnicianId(e.target.value)}
                      style={{
                        width: '100%',
                        padding: '10px 12px',
                        border: '1px solid var(--border-color)',
                        borderRadius: '8px',
                        fontSize: '14px'
                      }}
                    >
                      <option value="">Teknisyen seçin</option>
                      {technicians.map((technician) => {
                        const techId = technician.id || technician._id;
                        const techName = `${technician.ad || technician.name || ''} ${technician.soyad || technician.surname || ''}`.trim() || 'Teknisyen';
                        return (
                          <option key={techId} value={techId}>
                            {techName}
                          </option>
                        );
                      })}
                    </select>
                  </div>
                </>
              )}
            </div>
            <div className="modal-footer">
              <button
                type="button"
                className="secondary-btn"
                onClick={() => setIsTechnicianModalOpen(false)}
                disabled={submitting}
              >
                İptal
              </button>
              <button
                type="button"
                className="primary-btn"
                onClick={handleAssignTechnician}
                disabled={submitting || !selectedTechnicianId || techniciansLoading}
              >
                {submitting
                  ? hasTechnician
                    ? 'Değiştiriliyor...'
                    : 'Atanıyor...'
                  : hasTechnician
                    ? 'Teknisyeni Değiştir'
                    : 'Teknisyen Ataması Yap'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Atama İptal Modal */}
      {isCancelModalOpen && (
        <div className="modal-overlay" onClick={() => !submitting && setIsCancelModalOpen(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Atamayı İptal Et</h2>
              <button
                type="button"
                className="modal-close"
                onClick={() => !submitting && setIsCancelModalOpen(false)}
                disabled={submitting}
              >
                <i className="fas fa-times"></i>
              </button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label htmlFor="cancel-reason">
                  İptal Gerekçesi <span style={{ color: '#ef4444' }}>*</span>
                </label>
                <textarea
                  id="cancel-reason"
                  value={cancelReason}
                  onChange={(e) => setCancelReason(e.target.value)}
                  placeholder="İptal gerekçesini yazın (en az 10 karakter)"
                  required
                  minLength={10}
                  rows={5}
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    border: '1px solid var(--border-color)',
                    borderRadius: '8px',
                    fontSize: '14px',
                    fontFamily: 'inherit',
                    resize: 'vertical'
                  }}
                />
                {cancelReason && cancelReason.trim().length < 10 && (
                  <div style={{ color: '#ef4444', fontSize: '12px', marginTop: '4px' }}>
                    İptal gerekçesi en az 10 karakter olmalıdır
                  </div>
                )}
              </div>
            </div>
            <div className="modal-footer">
              <button
                type="button"
                className="secondary-btn"
                onClick={() => setIsCancelModalOpen(false)}
                disabled={submitting}
              >
                İptal
              </button>
              <button
                type="button"
                className="secondary-btn"
                onClick={handleCancelAssignment}
                disabled={submitting || !cancelReason || cancelReason.trim().length < 10}
                style={{ backgroundColor: '#ef4444', color: '#ffffff', borderColor: '#ef4444' }}
              >
                {submitting ? 'İptal Ediliyor...' : 'Atamayı İptal Et'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Parça Seç Modal - kategoriler, checkbox, fotoğraf / no-image ikonu + tooltip */}
      <ParcaSecModal
        isOpen={isParcaSecModalOpen}
        onClose={() => setIsParcaSecModalOpen(false)}
        servisKaydiId={getIds().srId}
        onSeçilenleriEkle={handleParcaSecilenleriEkle}
      />
    </main>
  );
};

export default TechServiceAppointmentDetailPage;

